<template>
  <div class="modal-overlay" @click.self="closeModal" role="dialog" aria-modal="true" :aria-labelledby="titleId" :aria-describedby="messageId">
    <div class="modal-content-box" :class="`theme-${selectedRole}`" ref="modalContent">
      <button class="close-button" @click="closeModal" aria-label="Close modal">&times;</button>
      <p class="title" :id="titleId">Join PALLAS</p>
      <p class="message" :id="messageId">Create your profile and connect art with science.</p>
      
      <!-- Role Selection -->
      <div class="role-selection" role="group" aria-labelledby="role-heading">
        <h2 id="role-heading" class="visually-hidden">Select your role</h2>
        <RoleSlider v-model="selectedRole" />
      </div>

      <form @submit.prevent="signUp" class="flex flex-column">
        <div class="flex">
          <div class="form-group">
            <label class="static-label" for="firstName-input">First Name *</label>
            <input
              id="firstName-input"
              :class="['input-field', `role-${selectedRole}`, { 'input-error': errors.firstName }]"
              type="text"
              v-model="firstName"
              @input="validateFirstName"
              @keydown.enter="signUp"
              placeholder="Enter your first name"
              :aria-invalid="!!errors.firstName"
              :aria-describedby="errors.firstName ? 'firstName-error' : null"
              required
              aria-required="true"
              ref="firstInput"
            />
          </div>
          <div class="form-group">
            <label class="static-label" for="lastName-input">Last Name *</label>
            <input
              id="lastName-input"
              :class="['input-field', `role-${selectedRole}`, { 'input-error': errors.lastName }]"
              type="text"
              v-model="lastName"
              @input="validateLastName"
              @keydown.enter="signUp"
              placeholder="Enter your last name"
              :aria-invalid="!!errors.lastName"
              :aria-describedby="errors.lastName ? 'lastName-error' : null"
              required
              aria-required="true"
            />
          </div>
        </div>

        <div class="form-group">
          <label class="static-label" for="email-input">Email *</label>
          <input
            id="email-input"
            :class="['input-field', `role-${selectedRole}`, { 'input-error': errors.email }]"
            type="email"
            v-model="email"
            @input="validateEmail"
            @keydown.enter="signUp"
            placeholder="Enter your email"
            :aria-invalid="!!errors.email"
            :aria-describedby="errors.email ? 'email-error' : null"
            required
            aria-required="true"
            autocomplete="email"
          />
        </div>

        <div class="form-group password-field">
          <label class="static-label" for="password-input">Password *</label>
          <div class="password-input-wrapper">
            <input
              id="password-input"
              :class="['input-field', `role-${selectedRole}`, { 'input-error': errors.password }]"
              :type="showPassword ? 'text' : 'password'"
              v-model="password"
              @input="validatePassword"
              @keydown.enter="signUp"
              placeholder="Enter your password"
              :aria-invalid="!!errors.password"
              :aria-describedby="errors.password ? 'password-error' : 'password-toggle-desc'"
              required
              aria-required="true"
              autocomplete="new-password"
            />
            <button
              type="button"
              class="password-toggle"
              @click="showPassword = !showPassword"
              @keydown.enter="showPassword = !showPassword"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
              :aria-pressed="showPassword"
              aria-controls="password-input"
              tabindex="0"
            >
              <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye-icon lucide-eye"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/><circle cx="12" cy="12" r="3"/></svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye-off-icon lucide-eye-off"><path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"/><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"/><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"/><path d="m2 2 20 20"/></svg>
            </button>
          </div>
        </div>

        <div class="form-group password-field">
          <label class="static-label" for="confirmPassword-input">Confirm Password *</label>
          <div class="password-input-wrapper">
            <input
              id="confirmPassword-input"
              :class="['input-field', `role-${selectedRole}`, { 'input-error': errors.confirmPassword }]"
              :type="showConfirmPassword ? 'text' : 'password'"
              v-model="confirmPassword"
              @input="validateConfirmPassword"
              @keydown.enter="signUp"
              placeholder="Confirm your password"
              :aria-invalid="!!errors.confirmPassword"
              :aria-describedby="errors.confirmPassword ? 'confirmPassword-error' : 'confirmPassword-toggle-desc'"
              required
              aria-required="true"
              autocomplete="new-password"
            />
            <button
              type="button"
              class="password-toggle"
              @click="showConfirmPassword = !showConfirmPassword"
              @keydown.enter="showConfirmPassword = !showConfirmPassword"
              :aria-label="showConfirmPassword ? 'Hide password' : 'Show password'"
              :aria-pressed="showConfirmPassword"
              aria-controls="confirmPassword-input"
              tabindex="0"
            >
              <svg v-if="showConfirmPassword" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye-icon lucide-eye"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/><circle cx="12" cy="12" r="3"/></svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye-off-icon lucide-eye-off"><path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"/><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"/><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"/><path d="m2 2 20 20"/></svg>
            </button>
          </div>
        </div>

        <div v-if="errors.firstName" class="field-error" id="firstName-error" role="alert" aria-live="polite">{{ errors.firstName }}</div>
        <div v-if="errors.lastName" class="field-error" id="lastName-error" role="alert" aria-live="polite">{{ errors.lastName }}</div>
        <div v-if="errors.email" class="field-error" id="email-error" role="alert" aria-live="polite">{{ errors.email }}</div>
        <div v-if="errors.password" class="field-error" id="password-error" role="alert" aria-live="polite">{{ errors.password }}</div>
        <div v-if="errors.confirmPassword" class="field-error" id="confirmPassword-error" role="alert" aria-live="polite">{{ errors.confirmPassword }}</div>
        <div id="password-toggle-desc" class="visually-hidden">Press to toggle password visibility</div>
        <div id="confirmPassword-toggle-desc" class="visually-hidden">Press to toggle password confirmation visibility</div>

        <div class="terms-container">
          <input type="checkbox" id="terms" v-model="agreeToTerms" class="terms-checkbox" aria-required="true" :aria-invalid="!agreeToTerms && isSubmitting">
          <label for="terms" class="terms-label">
            I agree to the <button type="button" class="terms-link link-button" @click="showTerms" tabindex="0">terms and conditions</button>
          </label>
        </div>

        <button
          v-if="!isSubmitting && canSubmit"
          @click="signUp"
          @keydown.enter="signUp"
          class="simple-form-button"
          type="submit"
          tabindex="0"
        >
          <span>{{ isSubmitting ? 'Creating Account...' : 'Join PALLAS' }}</span>
        </button>
        <button
          v-else
          class="simple-form-button disabled"
          disabled
          type="button"
          aria-label="Cannot submit - please fill all required fields and agree to terms"
        >
          <span>{{ isSubmitting ? 'Creating Account...' : 'Join PALLAS' }}</span>
        </button>

        <div v-if="errorMessage" class="error-message" role="alert" aria-live="polite" aria-atomic="true">{{ errorMessage }}</div>
        <div v-if="successMessage" class="success-message" role="status" aria-live="polite" aria-atomic="true">{{ successMessage }}</div>

        <p class="signin">
          Already have an account?
          <button type="button" @click="switchToLogin" @keydown.enter="switchToLogin" class="link-button" tabindex="0">Login</button>
        </p>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { auth } from "../firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { db } from "../firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { closeModal, openLoginModal, signUpRole } from '@/composables/useAuthModal';
import RoleSlider from '@/components/RoleSlider.vue';

export default {
  name: "SignUpModal",
  components: {
    RoleSlider
  },
  setup() {
    const router = useRouter();
    const firstName = ref("");
    const lastName = ref("");
    const email = ref("");
    const password = ref("");
    const confirmPassword = ref("");
    const selectedRole = ref(signUpRole.value || 'both');
    
    // Watch for changes to signUpRole and update selectedRole
    watch(signUpRole, (newRole) => {
      if (typeof newRole === 'string' && ['artist', 'both', 'scientist'].includes(newRole)) {
        selectedRole.value = newRole;
      }
    });
    const agreeToTerms = ref(false);
    const showPassword = ref(false);
    const showConfirmPassword = ref(false);
    const isSubmitting = ref(false);
    const errorMessage = ref("");
    const successMessage = ref("");

    const errors = ref({
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
    });

    // Modal accessibility
    const modalContent = ref(null);
    const firstInput = ref(null);
    const titleId = `signup-title-${Math.random().toString(36).substr(2, 9)}`;
    const messageId = `signup-message-${Math.random().toString(36).substr(2, 9)}`;
    const previousFocusElement = ref(null);

    const canSubmit = computed(() => {
      return !isSubmitting.value && 
             firstName.value !== '' && 
             lastName.value !== '' && 
             email.value !== '' && 
             password.value !== '' && 
             confirmPassword.value !== '' &&
             agreeToTerms.value &&
             !errors.value.firstName &&
             !errors.value.lastName &&
             !errors.value.email &&
             !errors.value.password &&
             !errors.value.confirmPassword;
    });

    const validateFirstName = () => {
      if (!firstName.value.trim()) {
        errors.value.firstName = "First name is required.";
      } else {
        errors.value.firstName = "";
      }
    };

    const validateLastName = () => {
      if (!lastName.value.trim()) {
        errors.value.lastName = "Last name is required.";
      } else {
        errors.value.lastName = "";
      }
    };

    const validateEmail = () => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email.value) {
        errors.value.email = "Email is required.";
      } else if (!emailRegex.test(email.value)) {
        errors.value.email = "Please enter a valid email address.";
      } else {
        errors.value.email = "";
      }
    };

    const validatePassword = () => {
      if (password.value.length < 6) {
        errors.value.password = "Password must be at least 6 characters.";
      } else if (!/[!@#$%^&*]/.test(password.value)) {
        errors.value.password = "Password must include at least one special character.";
      } else {
        errors.value.password = "";
      }
      validateConfirmPassword(); // Re-check confirm password when password changes
    };

    const validateConfirmPassword = () => {
      if (!confirmPassword.value) {
        errors.value.confirmPassword = "Please confirm your password.";
      } else if (confirmPassword.value !== password.value) {
        errors.value.confirmPassword = "Passwords do not match.";
      } else {
        errors.value.confirmPassword = "";
      }
    };

    const signUp = async () => {
      // Validate all fields
      validateFirstName();
      validateLastName();
      validateEmail();
      validatePassword();
      validateConfirmPassword();

      if (!canSubmit.value) {
        return;
      }

      isSubmitting.value = true;
      errorMessage.value = "";
      successMessage.value = "";

      try {
        const userCredential = await createUserWithEmailAndPassword(auth, email.value, password.value);
        const user = userCredential.user;

        // Ensure role is a valid string
        const validRole = typeof selectedRole.value === 'string' && 
                         ['artist', 'both', 'scientist'].includes(selectedRole.value) 
                         ? selectedRole.value 
                         : 'both';

        await setDoc(doc(db, "users", user.uid), {
          firstName: firstName.value.trim(),
          lastName: lastName.value.trim(),
          email: email.value.toLowerCase(),
          role: validRole,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          // Default to visible and open to collaborations when profile becomes complete
          profileVisible: true,
          openToCollaborations: true,
          verified: false, // Admin must verify before showing in gallery
        });

        localStorage.setItem('user-info', JSON.stringify({
          uid: user.uid,
          email: email.value.toLowerCase(),
          firstName: firstName.value.trim(),
          lastName: lastName.value.trim(),
          role: validRole,
        }));

        successMessage.value = "Account created successfully! Welcome to PALLAS!";

        setTimeout(async () => {
          closeModal();
          await nextTick();
          router.push('/profile-dashboard');
        }, 1500);

      } catch (error) {
        console.error("Sign-up error:", error);
        switch (error.code) {
          case "auth/email-already-in-use":
            errorMessage.value = "An account with this email already exists.";
            break;
          case "auth/invalid-email":
            errorMessage.value = "Please enter a valid email address.";
            break;
          case "auth/weak-password":
            errorMessage.value = "Password is too weak. Please choose a stronger password.";
            break;
          default:
            errorMessage.value = "Failed to create account. Please try again.";
        }
      } finally {
        isSubmitting.value = false;
      }
    };

    const switchToLogin = () => {
      closeModal();
      openLoginModal();
    };

    const showTerms = () => {
      // Placeholder for terms modal
    };

    // Focus management
    const setupFocusManagement = () => {
      previousFocusElement.value = document.activeElement;

      nextTick(() => {
        if (firstInput.value) {
          firstInput.value.focus();
        }
      });
    };

    const trapFocus = (event) => {
      if (!modalContent.value) return;

      const focusableElements = modalContent.value.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.key === 'Tab') {
        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      } else {
        trapFocus(event);
      }
    };

    const restoreFocus = () => {
      if (previousFocusElement.value) {
        previousFocusElement.value.focus();
      }
    };

    // Set up focus when component mounts
    onMounted(() => {
      setupFocusManagement();
      window.addEventListener('keydown', handleKeydown);
    });

    onUnmounted(() => {
      window.removeEventListener('keydown', handleKeydown);
      restoreFocus();
    });

    return {
      firstName,
      lastName,
      email,
      password,
      confirmPassword,
      selectedRole,
      agreeToTerms,
      showPassword,
      showConfirmPassword,
      isSubmitting,
      errorMessage,
      successMessage,
      errors,
      canSubmit,
      signUp,
      switchToLogin,
      closeModal,
      validateFirstName,
      validateLastName,
      validateEmail,
      validatePassword,
      validateConfirmPassword,
      showTerms,
      modalContent,
      firstInput,
      titleId,
      messageId,
    };
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

.close-button {
  position: absolute;
  top: 15px;
  right: 20px;
  background: none;
  border: none;
  font-size: 28px;
  color: var(--textColor);
  cursor: pointer;
  transition: color 0.3s ease;
}

.close-button:hover {
  color: var(--sciColor);
}

.modal-content-box {
  display: flex;
  flex-direction: column;
  gap: var(--paddingStandard);
  max-width: 460px;
  width: 90%;
  padding: var(--paddingSpaceous);
  border-radius: var(--stdRadius);
  position: relative;
  background: var(--bckColor);
  color: var(--textColor);
  box-shadow: var(--stdShadow);
  border: 1px solid var(--borderColor);
  animation: slideUp 0.4s ease;
}

.title {
  font-size: var(--subTitle);
  font-weight: 700;
  text-align: center;
  background: var(--art-sci-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin-bottom: 4px;
  letter-spacing: 0.02em;
}

.message {
  font-size: var(--mainSubText);
  color: var(--textColor);
  text-align: center;
  line-height: 1.4;
  margin-bottom: 8px;
}

.role-selection {
  margin-bottom: 8px;
}

.role-title {
  font-size: var(--labelText);
  font-weight: 600;
  text-align: center;
  margin-bottom: var(--paddingTight);
  color: var(--textColor);
}


.flex {
  display: flex;
  width: 100%;
  gap: var(--paddingStandard);
}

.flex-column {
  flex-direction: column;
}

.form-group {
  margin-bottom: var(--paddingStandard);
  flex: 1;
}

.static-label {
  display: block;
  font-size: var(--labelText);
  font-weight: 600;
  color: var(--textColor);
  margin-bottom: var(--paddingTight);
}

.input-field {
  background: var(--bckColor);
  border: 1px solid var(--borderColor);
  border-radius: var(--stdRadius);
  padding: 11px 14px;
  width: 100%;
  font-size: 14px;
  font-family: var(--font-primary);
  font-weight: 500;
  color: var(--textColor);
  outline: 0;
  transition: all 0.3s ease;
}

.password-input-wrapper {
  position: relative;
}

.input-field:focus {
  outline: none;
  border: 1px solid var(--blendColor);
}

.input-field.role-artist:focus {
  border: 1px solid var(--artColor);
}

.input-field.role-scientist:focus {
  border: 1px solid var(--sciColor);
}

.input-field.role-both:focus {
  border: 1px solid var(--blendColor);
}

.input-error {
  border: 1px solid var(--errorColor) !important;
}

.form-group:has(.input-error) .static-label {
  color: var(--errorColor) !important;
}

.password-field {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--textColor);
  cursor: pointer;
  padding: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-toggle:hover {
  color: var(--sciColor);
}

.password-toggle svg {
  width: 20px;
  height: 20px;
}

.terms-container {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 4px 0;
}

.terms-checkbox {
  margin-top: 2px;
}

.terms-label {
  font-size: var(--tagText);
  color: var(--textColor);
  line-height: 1.3;
}

.terms-link {
  color: var(--blendColor);
  font-weight: bold;
  text-decoration: none;
}

.terms-link:hover {
  text-decoration: underline;
}

.simple-form-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  background: var(--bckColor);
  border: 2px solid var(--blendColor);
  border-radius: var(--stdRadius);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  position: relative;
  margin-top: var(--paddingTight);
  font-family: var(--font-primary);
  transition: all 0.3s ease;
  color: var(--textColor);
}

.simple-form-button:hover:not(.disabled) {
  transform: translateY(-2px);
  border-color: var(--sciColor);
  box-shadow: 0 4px 12px rgba(101, 102, 190, 0.2);
}

.simple-form-button.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: var(--neutralColor);
  border-color: #ccc;
}

.field-error {
  color: var(--errorColor);
  font-size: var(--tagText);
  margin-top: -var(--paddingTight);
  margin-bottom: var(--paddingTight);
}

.error-message {
  color: var(--errorColor);
  font-size: var(--labelText);
  text-align: center;
  margin-top: var(--paddingTight);
}

.success-message {
  color: var(--successColor);
  font-size: var(--labelText);
  text-align: center;
  margin-top: var(--paddingTight);
}

.signin {
  font-size: var(--mainSubText);
  color: var(--textColor);
  text-align: center;
  margin-top: 8px;
}

.signin a {
  color: var(--blendColor);
  font-weight: bold;
  text-decoration: none;
}

.signin a:hover {
  background: var(--art-sci-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  text-decoration: underline;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Accessibility improvements */
.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.link-button {
  background: none;
  border: none;
  color: var(--blendColor);
  font-weight: bold;
  text-decoration: none;
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;
  padding: 0;
  margin: 0;
  display: inline;
}

.link-button:hover,
.link-button:focus {
  background: var(--art-sci-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  text-decoration: underline;
  outline: none;
}

.terms-link {
  background: none;
  border: none;
  color: var(--blendColor);
  font-weight: bold;
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;
  padding: 0;
  margin: 0;
  display: inline;
}

/* Enhanced focus styles */
.input-field:focus {
  outline: 2px solid var(--blendColor);
  outline-offset: 2px;
  border: 1px solid var(--blendColor);
}

.simple-form-button:focus {
  outline: 2px solid var(--sciColor);
  outline-offset: 2px;
}

.close-button:focus {
  outline: 2px solid var(--sciColor);
  outline-offset: 2px;
}

.password-toggle:focus {
  outline: 2px solid var(--blendColor);
  outline-offset: 2px;
}

.terms-checkbox:focus {
  outline: 2px solid var(--blendColor);
  outline-offset: 2px;
}

/* Mobile responsive improvements */
.modal-content-box {
  max-height: 90vh;
  overflow-y: auto;
}

@media (max-width: 480px) {
  .modal-content-box {
    width: 95%;
    padding: 1rem;
    margin: 1rem;
    max-height: 85vh;
  }

  .input-field {
    font-size: 16px; /* Prevents zoom on iOS */
    padding: 12px 14px;
  }

  .form-group {
    margin-bottom: 1rem;
  }

  .simple-form-button {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
    min-height: 44px; /* Touch target size */
  }

  .close-button {
    font-size: 24px;
    top: 12px;
    right: 16px;
    min-width: 44px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .password-input-container .toggle-password {
    min-width: 44px;
    min-height: 44px;
  }

  .terms-checkbox {
    min-width: 44px;
    min-height: 44px;
  }

  .terms-label {
    font-size: 0.9rem;
    line-height: 1.4;
  }
}
</style>